import type { ReactNode } from 'react';
import {
  useFieldArray,
  type ArrayPath,
  type Control,
  type FieldArray,
  type FieldArrayWithId,
  type FieldValues,
} from 'react-hook-form';
import { ArrowDown, ArrowUp, CopyPlus, Plus, Trash2 } from 'lucide-react';
import { cx } from '@/lib/cx';
import { IconButton } from './Button';

export interface SubTableColumn<TFieldValues extends FieldValues, TName extends ArrayPath<TFieldValues>> {
  key: string;
  header: ReactNode;
  width?: number | string;
  required?: boolean;
  align?: 'left' | 'center' | 'right';
  /**
   * セルの入力要素を描画する。
   * `path` は `明細名.行番号` 形式（例: `items.0`）。`register(`${path}.amount`)` のように使う。
   */
  render: (ctx: { index: number; path: `${TName}.${number}`; field: FieldArrayWithId<TFieldValues, TName> }) => ReactNode;
}

export interface SubTableEditorProps<TFieldValues extends FieldValues, TName extends ArrayPath<TFieldValues>> {
  control: Control<TFieldValues>;
  name: TName;
  columns: ReadonlyArray<SubTableColumn<TFieldValues, TName>>;
  /** 「行を追加」で追加する空行 */
  createRow: () => FieldArray<TFieldValues, TName>;
  title?: ReactNode;
  addLabel?: string;
  minRows?: number;
  maxRows?: number;
  /** 行の並べ替え（↑↓）を許可（既定 true） */
  reorderable?: boolean;
  /** 行の複製ボタンを表示 */
  duplicable?: boolean;
  readOnly?: boolean;
  /** 合計行など（tfoot 内に描画。列数は columns.length + 2） */
  footer?: ReactNode;
  emptyMessage?: string;
  /** 明細全体のエラー（例: 「1 行以上入力してください」） */
  error?: string;
}

/**
 * 明細（サブテーブル）入力。react-hook-form の useFieldArray と連携する。
 * kintone のテーブルフィールドに相当。行追加・削除・並べ替え・複製に対応。
 */
export function SubTableEditor<TFieldValues extends FieldValues, TName extends ArrayPath<TFieldValues>>({
  control,
  name,
  columns,
  createRow,
  title,
  addLabel = '行を追加',
  minRows = 0,
  maxRows,
  reorderable = true,
  duplicable = false,
  readOnly = false,
  footer,
  emptyMessage = '明細がありません。「' + addLabel + '」から入力してください。',
  error,
}: SubTableEditorProps<TFieldValues, TName>) {
  const { fields, append, remove, move, insert } = useFieldArray<TFieldValues, TName>({ control, name });
  const canAdd = !readOnly && (maxRows == null || fields.length < maxRows);
  const canRemove = !readOnly && fields.length > minRows;

  return (
    <div className={cx('bs-subtable', error && 'has-error')}>
      {(title || canAdd) && (
        <div className="bs-subtable__head">
          {title && <div className="bs-subtable__title">{title}</div>}
          <div className="bs-subtable__count bs-num">{fields.length} 行</div>
        </div>
      )}
      <div className="bs-table-scroll">
        <table className="bs-table bs-subtable__table">
          <colgroup>
            <col style={{ width: 44 }} />
            {columns.map((c) => (
              <col key={c.key} style={c.width != null ? { width: c.width } : undefined} />
            ))}
            {!readOnly && <col style={{ width: reorderable ? (duplicable ? 132 : 104) : duplicable ? 76 : 48 }} />}
          </colgroup>
          <thead>
            <tr>
              <th className="is-center">No.</th>
              {columns.map((c) => (
                <th key={c.key} className={`is-${c.align ?? 'left'}`}>
                  {c.header}
                  {c.required && <span className="bs-field__req bs-field__req--mini">必須</span>}
                </th>
              ))}
              {!readOnly && <th className="is-center">操作</th>}
            </tr>
          </thead>
          <tbody>
            {fields.map((field, index) => {
              const path = `${name}.${index}` as `${TName}.${number}`;
              return (
                <tr key={field.id}>
                  <td className="is-center bs-subtable__no bs-num">{index + 1}</td>
                  {columns.map((c) => (
                    <td key={c.key} className={`is-${c.align ?? 'left'}`}>
                      {c.render({ index, path, field })}
                    </td>
                  ))}
                  {!readOnly && (
                    <td className="is-center">
                      <div className="bs-subtable__ops">
                        {reorderable && (
                          <>
                            <IconButton size="sm" label={`${index + 1}行目を上へ`} icon={<ArrowUp size={15} />} disabled={index === 0} onClick={() => move(index, index - 1)} />
                            <IconButton
                              size="sm"
                              label={`${index + 1}行目を下へ`}
                              icon={<ArrowDown size={15} />}
                              disabled={index === fields.length - 1}
                              onClick={() => move(index, index + 1)}
                            />
                          </>
                        )}
                        {duplicable && (
                          <IconButton
                            size="sm"
                            label={`${index + 1}行目を複製`}
                            icon={<CopyPlus size={15} />}
                            disabled={!canAdd}
                            onClick={() => {
                              const { id: _id, ...copy } = field as Record<string, unknown>;
                              insert(index + 1, copy as FieldArray<TFieldValues, TName>);
                            }}
                          />
                        )}
                        <IconButton
                          size="sm"
                          variant="danger"
                          label={`${index + 1}行目を削除`}
                          icon={<Trash2 size={15} />}
                          disabled={!canRemove}
                          onClick={() => remove(index)}
                        />
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
          {footer && <tfoot>{footer}</tfoot>}
        </table>
        {fields.length === 0 && <div className="bs-table-empty">{emptyMessage}</div>}
      </div>
      {error && (
        <div className="bs-field__desc is-error" role="alert">
          {error}
        </div>
      )}
      {canAdd && (
        <button type="button" className="bs-subtable__add" onClick={() => append(createRow())}>
          <Plus size={15} aria-hidden />
          {addLabel}
        </button>
      )}
    </div>
  );
}

